import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { confirm } from '@dashboard/lib/confirm';
import { fetcher } from '@dashboard/services/request';
import { useTicketGroupEdit } from './TicketGroupEditContext';
import usePage from '@dashboard/hooks/usePage';
import { useTicketGroups } from './TicketGroupsContext';

export default function useTicketTypeHelpers(ticketTypeId) {
  const { token, sellerId } = useAuth();
  const { refetch: refetchPage } = usePage();
  const { state: ticketGroups, dispatch } = useTicketGroups();
  const {
    editingTicketTypeId,
    setHasUnsavedChanges,
    hasUnsavedChanges,
    isAddingTicketType,
    setIsAddingTicketType,
    setEditingTicketTypeId,
    setIsEditingTicketGroupSettings,
    setIsEditingTicketGroupLayout,
  } = useTicketGroupEdit();

  const ticketType = ticketGroups
    .flatMap(group => group.ticketTypes)
    .find(t => t._id === ticketTypeId);

  const isEditingTicketType = editingTicketTypeId === ticketTypeId;

  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);

  function onDirty(dirty) {
    if (isEditingTicketType || isAddingTicketType) {
      setHasUnsavedChanges(dirty);
    }
  }

  async function onAddTicketType() {
    let proceed = true;
    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }
    if (!proceed) return;

    setEditingTicketTypeId(null);
    setHasUnsavedChanges(false);
    setIsEditingTicketGroupSettings(false);
    setIsEditingTicketGroupLayout(false);
    setIsAddingTicketType(true);
  }

  async function onEditTicketType(ticketTypeId) {
    let proceed = true;

    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    setIsAddingTicketType(false);
    setEditingTicketTypeId(ticketTypeId);
    setIsEditingTicketGroupSettings(false);
    setIsEditingTicketGroupLayout(false);
  }

  async function onCancelEditTicketType() {
    setEditingTicketTypeId(null);
    setHasUnsavedChanges(false);
    setIsAddingTicketType(false);
  }

  const deleteTicketTypeMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/ticket-types/${ticketTypeId}`, token, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      dispatch({ type: 'deleteTicketType', ticketGroupId: ticketType.ticketGroup, ticketTypeId });
      refetchPage();
      setEditingTicketTypeId(null);
      setHasUnsavedChanges(false);
      setIsAddingTicketType(false);
    },
  });

  async function onDeleteTicketType() {
    const proceed = await confirm('Delete ticket type?', {
      confirm: 'Delete',
    });

    if (!proceed) return;

    await deleteTicketTypeMutation.mutateAsync();
  }

  const updateTicketTypeStatus = useMutation({
    mutationFn: status =>
      fetcher(`/sellers/${sellerId}/ticket-types/${ticketTypeId}/status`, token, {
        method: 'PUT',
        body: JSON.stringify({
          status: status,
        }),
      }),
    onSuccess: data => {
      dispatch({
        type: 'updateTicketType',
        ticketGroupId: ticketType.ticketGroup,
        ticketTypeId,
        values: data,
      });

      refetchPage();
    },
  });

  async function onEnableDisableTicketType() {
    const newStatus = ticketType.status === 'enabled' ? 'disabled' : 'enabled';

    await updateTicketTypeStatus.mutateAsync(newStatus);
  }

  async function onArchiveTicketType() {
    const proceed = await confirm('Archive ticket type?', {
      description:
        "Archived tickets don't count towards the event's capacity. If you want to want to hide the ticket type, you can disable it instead.",
      confirm: 'Archive',
    });

    if (!proceed) return;

    await updateTicketTypeStatus.mutateAsync('archived');
  }

  const duplicateTicketTypeMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/ticket-types/${ticketTypeId}/duplicate`, token, {
        method: 'POST',
      }),
    onSuccess: data => {
      const ticketGroup = ticketGroups.find(tg => tg._id === ticketType?.ticketGroup);
      const newIndex = ticketGroup?.ticketTypes?.findIndex(t => t._id === ticketTypeId) + 1;

      dispatch({
        type: 'addTicketType',
        ticketGroupId: ticketType.ticketGroup,
        values: data,
        index: newIndex,
      });

      refetchPage();
    },
  });

  async function onDuplicateTicketType() {
    await duplicateTicketTypeMutation.mutateAsync();
  }

  const moveTicketTypeMutation = useMutation({
    mutationFn: newTicketGroupId =>
      fetcher(`/sellers/${sellerId}/ticket-types/${ticketTypeId}/move-group`, token, {
        method: 'POST',
        body: JSON.stringify({
          ticketGroupId: newTicketGroupId,
        }),
      }),
    onSuccess: data => {
      // Update the local state by removing from old group and adding to new group
      const oldTicketGroup = ticketGroups.find(tg =>
        tg.ticketTypes.some(tt => tt._id === ticketTypeId),
      );

      if (oldTicketGroup) {
        // Remove from old group
        dispatch({
          type: 'deleteTicketType',
          ticketGroupId: oldTicketGroup._id,
          ticketTypeId: ticketTypeId,
        });

        // Add to new group
        dispatch({
          type: 'addTicketType',
          ticketGroupId: data.ticketGroup,
          values: data,
        });
      }

      refetchPage();
    },
  });

  function onMoveTicketType() {
    if (!canMoveTicketType(ticketType)) {
      return;
    }
    setIsMoveDialogOpen(true);
  }

  async function handleMoveTicketType(targetGroupId) {
    await moveTicketTypeMutation.mutateAsync(targetGroupId);
  }

  // Get available groups for move dialog
  const currentGroup = ticketGroups.find(tg => tg.ticketTypes.some(tt => tt._id === ticketTypeId));
  const availableGroups = ticketGroups.filter(tg => tg._id !== currentGroup?._id);

  const canMoveTicketType = ticketType => {
    return availableGroups.length > 0 && ticketType?.quantitySold === 0;
  };

  return {
    onDirty,
    onAddTicketType,
    onEditTicketType,
    onCancelEditTicketType,
    onDeleteTicketType,
    onEnableDisableTicketType,
    onArchiveTicketType,
    onDuplicateTicketType,
    onMoveTicketType,
    handleMoveTicketType,
    isMoveDialogOpen,
    setIsMoveDialogOpen,
    availableGroups,
    isMoving: moveTicketTypeMutation.isPending,
    canMoveTicketType,
  };
}
